import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import useTheme from "../hooks/useTheme";

export const cardBrandIcons = {
  visa: "fab fa-cc-visa",
  mastercard: "fab fa-cc-mastercard",
  amex: "fab fa-cc-amex",
  discover: "fab fa-cc-discover",
  diners: "fab fa-cc-diners-club",
  jcb: "fab fa-cc-jcb",
  default: "far fa-credit-card",
};

export const buildStripeAppearance = (resolvedTheme = "light") => {
  const dark = resolvedTheme === "dark";
  return {
    theme: dark ? "night" : "stripe",
    variables: {
      fontFamily: "Open Sans, system-ui, sans-serif",
      fontSizeBase: "14px",
      borderRadius: "4px",
      colorDanger: "#dc3545",
      ...(dark
        ? {
            colorBackground: "#161B26",
            colorText: "#F5F6F8",
            colorTextSecondary: "#8B92A0",
            colorTextPlaceholder: "#8B92A0",
          }
        : {}),
    },
    rules: {
      ".Input": {
        boxShadow: "none",
        border: dark ? "1px solid #333B4A" : "1px solid #B3B3B3",
      },
    },
  };
};

export const stripeAppearance = buildStripeAppearance("light");

/**
 * PaymentMethodCard - Displays a single payment method
 *
 * Props:
 * - paymentMethod: { id, brand, last4, expMonth, expYear, isPending }
 * - isDefault: boolean - whether this is the default payment method
 * - selected: boolean - whether this card is selected (for selection mode)
 * - onSelect: function - callback when card is selected (enables selection mode)
 * - onDelete: function - callback when delete is clicked (enables delete mode)
 * - deleting: boolean - whether delete is in progress
 */
export const PaymentMethodCard = ({
  paymentMethod,
  isDefault,
  selected,
  onSelect,
  onDelete,
  deleting,
}) => {
  const brandIcon = cardBrandIcons[paymentMethod.brand] || cardBrandIcons.default;
  const isPending = paymentMethod.isPending;
  const isSelectable = !!onSelect;
  const isDeletable = !!onDelete;

  if (isSelectable) {
    return (
      <div
        onClick={() => !isPending && onSelect(paymentMethod.id)}
        style={{
          display: "flex",
          alignItems: "center",
          padding: "12px 16px",
          border: selected ? "2px solid var(--sl-fg-brand, #1982C4)" : "1px solid var(--sl-border-primary, #D5D7DA)",
          borderRadius: 8,
          cursor: isPending ? "not-allowed" : "pointer",
          backgroundColor: selected ? "var(--sl-bg-active, #f4f9fc)" : "var(--sl-bg-primary, #FFFFFF)",
          gap: 12,
          opacity: isPending ? 0.6 : 1,
        }}
      >
        <input
          type="radio"
          checked={selected}
          onChange={() => !isPending && onSelect(paymentMethod.id)}
          disabled={isPending}
          style={{ margin: 0 }}
        />
        {isPending ? (
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, color: "var(--sl-text-primary, #181D27)" }} />
        ) : (
          <i className={brandIcon} style={{ fontSize: 24, color: "var(--sl-text-primary, #181D27)" }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 500, fontSize: 14 }}>
            {isPending
              ? "Loading card details..."
              : `${paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in ${paymentMethod.last4}`}
          </div>
          {!isPending && (
            <div style={{ fontSize: 12, color: "var(--sl-text-quaternary, #717680)" }}>
              Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex align-items-center justify-content-between rounded mb-3"
      style={{
        padding: "12px 16px",
        opacity: isPending ? 0.6 : 1,
        border: "1px solid var(--sl-border-secondary, #E9EAEB)",
        backgroundColor: isDefault ? "var(--sl-bg-secondary, #FAFAFA)" : "var(--sl-bg-primary, #FFFFFF)",
      }}
    >
      <div className="d-flex align-items-center" style={{ gap: 12 }}>
        {isPending ? (
          <i className="fas fa-spinner fa-spin" style={{ fontSize: 24, color: "var(--sl-text-primary, #181D27)" }} />
        ) : (
          <i className={brandIcon} style={{ fontSize: 24, color: "var(--sl-text-primary, #181D27)" }} />
        )}
        <div>
          <div className="font-weight-medium" style={{ fontSize: 14 }}>
            {isPending
              ? "Loading card details..."
              : `${paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1)} ending in ${paymentMethod.last4}`}
          </div>
          {!isPending && (
            <div className="small" style={{ color: "var(--sl-text-quaternary, #717680)" }}>
              Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
            </div>
          )}
        </div>
        {isDefault && (
          <span className="badge ml-2" style={{ backgroundColor: "var(--sl-bg-success-primary, #ECFDF3)", color: "var(--sl-text-success, #079455)" }}>Default</span>
        )}
      </div>
      {isDeletable && !isPending && (
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="btn btn-link p-1"
          style={{ cursor: deleting ? "not-allowed" : "pointer", color: "var(--sl-text-error, #D92D20)" }}
        >
          {deleting ? "Removing..." : "Remove"}
        </button>
      )}
    </div>
  );
};

/**
 * AddPaymentMethodForm - Stripe Elements form for adding a payment method
 * Must be wrapped in Stripe Elements provider
 *
 * Props:
 * - onSuccess: function(paymentMethodId) - called when payment method is added
 * - onCancel: function - called when cancel is clicked (optional)
 * - submitLabel: string - label for submit button (default: "Save Payment Method")
 */
export const AddPaymentMethodForm = ({ onSuccess, onCancel, submitLabel = "Save Payment Method" }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const { error: submitError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
    } else {
      onSuccess(setupIntent?.payment_method);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <PaymentElement />
      </div>
      {error && (
        <div
          className="py-2 px-3"
          style={{
            fontSize: 14,
            borderRadius: "var(--sl-radius-md, 8px)",
            backgroundColor: "var(--sl-bg-error-primary, #FEF3F2)",
            color: "var(--sl-text-error, #D92D20)",
            border: "1px solid var(--sl-border-error, #FDA29B)",
          }}
        >
          {error}
        </div>
      )}
      <div className="d-flex justify-content-end" style={{ gap: 12 }}>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={processing}
            className="team-btn-secondary"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={!stripe || processing}
          className="team-btn-primary"
        >
          {processing ? "Saving..." : submitLabel}
        </button>
      </div>
    </form>
  );
};

/**
 * PaymentMethods - Full payment methods management component
 *
 * Props:
 * - teamId: string - team ID for API calls
 * - stripePublishableKey: string - Stripe publishable key (optional, will fetch if not provided)
 * - onSuccess: function - called when a payment method is added
 * - onPaymentMethodsChange: function(paymentMethods, defaultId) - called when payment methods change
 * - mode: "manage" | "select" - display mode (default: "manage")
 *   - "manage": shows default badge, delete buttons
 *   - "select": shows radio buttons for selection
 * - selectedPaymentMethodId: string - currently selected payment method (for select mode)
 * - onSelectionChange: function(paymentMethodId) - called when selection changes (for select mode)
 * - compact: boolean - use compact styling (default: false)
 * - showHeader: boolean - show "Payment Methods" header (default: true in manage mode)
 * - showEmptyState: boolean - show empty state message (default: true in manage mode)
 * - showAddButton: boolean - show add payment method button (default: true)
 * - addButtonLabel: string - label for add button when no cards exist
 * - addAnotherLabel: string - label for add button when cards exist
 */
const PaymentMethods = ({
  teamId,
  stripePublishableKey: initialStripeKey,
  onSuccess,
  onPaymentMethodsChange,
  mode = "manage",
  selectedPaymentMethodId,
  onSelectionChange,
  compact = false,
  showHeader = mode === "manage",
  showEmptyState = mode === "manage",
  showAddButton = true,
  addButtonLabel = "Add Payment Method",
  addAnotherLabel = "+ Add another card",
}) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState(initialStripeKey);
  const [deletingId, setDeletingId] = useState(null);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);

  const { resolvedTheme } = useTheme();
  const inThemedPage = typeof document !== "undefined" && document.body.classList.contains("od-themed");
  const stripeTheme = inThemedPage ? resolvedTheme : "light";

  useEffect(() => {
    if (!stripePublishableKey) {
      const fetchStripeKey = async () => {
        try {
          const response = await client.get(`/teams/${teamId}/account_details.json`);
          const { stripePublishableKey: key } = response.data;
          if (key) {
            setStripePublishableKey(key);
          }
        } catch (error) {
          toastError("Failed to load Stripe configuration.");
        }
      };
      fetchStripeKey();
    }
  }, [teamId, stripePublishableKey]);

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  const fetchPaymentMethods = async (showLoadingState = true) => {
    if (showLoadingState) {
      setLoading(true);
    }
    try {
      const response = await client.get(`/teams/${teamId}/payment_methods`);
      const methods = response.data.paymentMethods || [];
      const defaultId = response.data.defaultPaymentMethodId;

      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);

      if (onPaymentMethodsChange) {
        onPaymentMethodsChange(methods, defaultId);
      }

      if (mode === "select" && methods.length > 0 && onSelectionChange && !selectedPaymentMethodId) {
        onSelectionChange(defaultId || methods[0].id);
      }
    } catch (error) {
      toastError("Failed to load payment methods");
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, [teamId]);

  const handleAddPaymentMethod = async () => {
    setAddingPaymentMethod(true);
    try {
      const response = await client.post(`/teams/${teamId}/create_setup_intent`);
      setClientSecret(response.data.clientSecret);
      setShowAddForm(true);
    } catch (error) {
      toastError("Failed to initialize payment form");
    } finally {
      setAddingPaymentMethod(false);
    }
  };

  const handlePaymentMethodAdded = async (paymentMethodId) => {
    const placeholderCard = {
      id: paymentMethodId || `pending-${Date.now()}`,
      brand: "card",
      last4: "****",
      expMonth: "**",
      expYear: "**",
      isPending: true,
    };

    setPaymentMethods((prev) => [...prev, placeholderCard]);
    setShowAddForm(false);
    setClientSecret(null);
    toastSuccess("Payment method added successfully");

    if (onSuccess) {
      onSuccess(paymentMethodId);
    }

    try {
      const response = await client.get(`/teams/${teamId}/payment_methods`);
      const methods = response.data.paymentMethods || [];
      const defaultId = response.data.defaultPaymentMethodId;

      setPaymentMethods(methods);
      setDefaultPaymentMethodId(defaultId);

      if (onPaymentMethodsChange) {
        onPaymentMethodsChange(methods, defaultId);
      }

      if (mode === "select" && onSelectionChange) {
        const newCard = methods.find(m => m.id === paymentMethodId) || methods[methods.length - 1];
        if (newCard) {
          onSelectionChange(newCard.id);
        }
      }
    } catch (error) {
      toastError("Failed to refresh payment methods. Please reload the page.");
    }
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    if (!window.confirm("Are you sure you want to remove this payment method? This action cannot be undone.")) {
      return;
    }
    setDeletingId(paymentMethodId);
    try {
      await client.delete(`/teams/${teamId}/payment_methods/${paymentMethodId}`);
      toastSuccess("Payment method removed");
      fetchPaymentMethods();
    } catch (error) {
      toastError("Failed to remove payment method");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setClientSecret(null);
  };

  if (loading) {
    return (
      <div className="py-4 text-center" style={{ color: "var(--sl-text-quaternary, #717680)" }}>
        Loading payment methods...
      </div>
    );
  }

  const hasPaymentMethods = paymentMethods.length > 0;
  const isSelectMode = mode === "select";

  const renderPaymentMethodList = () => {
    if (!hasPaymentMethods) {
      if (showEmptyState && !compact) {
        return (
          <div
            className="p-4 text-center rounded mb-3"
            style={{ backgroundColor: "var(--sl-bg-secondary, #FAFAFA)", color: "var(--sl-text-quaternary, #717680)" }}
          >
            <i className="far fa-credit-card d-block mb-3" style={{ fontSize: 32 }} />
            No payment methods on file
          </div>
        );
      }
      return null;
    }

    if (isSelectMode) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
          {paymentMethods.map((pm) => (
            <PaymentMethodCard
              key={pm.id}
              paymentMethod={pm}
              selected={selectedPaymentMethodId === pm.id}
              onSelect={onSelectionChange}
            />
          ))}
        </div>
      );
    }

    return (
      <div className={compact ? "" : "mb-3"}>
        {paymentMethods.map((pm) => (
          <PaymentMethodCard
            key={pm.id}
            paymentMethod={pm}
            isDefault={pm.id === defaultPaymentMethodId}
            onDelete={() => handleDeletePaymentMethod(pm.id)}
            deleting={deletingId === pm.id}
          />
        ))}
      </div>
    );
  };

  const renderAddButton = () => {
    if (!showAddButton) return null;
    if (showAddForm) return null;

    if (!hasPaymentMethods) {
      return (
        <button
          type="button"
          onClick={handleAddPaymentMethod}
          disabled={addingPaymentMethod}
          className="team-btn-secondary"
          style={isSelectMode ? { marginBottom: 16 } : {}}
        >
          {addingPaymentMethod ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2" />
              Loading...
            </>
          ) : (
            <>
              <i className="fas fa-plus mr-2" />
              {addButtonLabel}
            </>
          )}
        </button>
      );
    }

    if (isSelectMode) {
      return (
        <button
          type="button"
          onClick={handleAddPaymentMethod}
          disabled={addingPaymentMethod}
          style={{
            background: "none",
            border: "none",
            color: "var(--sl-fg-brand, #1982C4)",
            cursor: addingPaymentMethod ? "not-allowed" : "pointer",
            fontSize: 14,
            padding: 0,
            marginBottom: 16,
          }}
        >
          {addingPaymentMethod ? "Loading..." : addAnotherLabel}
        </button>
      );
    }

    return (
      <button
        type="button"
        onClick={handleAddPaymentMethod}
        disabled={addingPaymentMethod}
        className="team-btn-secondary"
      >
        {addingPaymentMethod ? (
          <>
            <i className="fas fa-spinner fa-spin mr-2" />
            Loading...
          </>
        ) : (
          <>
            <i className="fas fa-plus mr-2" />
            {addButtonLabel}
          </>
        )}
      </button>
    );
  };

  const renderAddForm = () => {
    if (!showAddForm || !clientSecret || !stripePromise) return null;

    return (
      <div
        className={compact ? "" : "p-4 rounded"}
        style={{
          ...(isSelectMode ? { marginBottom: 16 } : {}),
          ...(compact ? {} : {
            border: "1px solid var(--sl-border-secondary, #E9EAEB)",
            backgroundColor: "var(--sl-bg-primary, #FFFFFF)",
          }),
        }}
      >
        {!compact && !isSelectMode && <h6 className="font-weight-bold mb-3">Add Payment Method</h6>}
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            fonts: [
              {
                cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans:400,600,700",
              },
            ],
            appearance: buildStripeAppearance(stripeTheme),
          }}
        >
          <AddPaymentMethodForm
            onSuccess={handlePaymentMethodAdded}
            onCancel={hasPaymentMethods || !isSelectMode ? handleCancel : null}
            submitLabel={isSelectMode ? "Add Card" : "Save Payment Method"}
          />
        </Elements>
      </div>
    );
  };

  return (
    <div>
      {showHeader && !compact && <h5 className="font-weight-bold mb-3">Payment Methods</h5>}

      {isSelectMode && (
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>
          {hasPaymentMethods ? "Select a payment method" : "Add a payment method"}
        </div>
      )}

      {!showAddForm && renderPaymentMethodList()}
      {renderAddForm()}
      {!showAddForm && renderAddButton()}
    </div>
  );
};

export default PaymentMethods;
